import React, { useEffect, useRef } from 'react';
import { FilterChip } from '@/components/ui/FilterChip';
import styles from './styles.module.scss';

export interface RoundPillsProps {
  /** One pill per round, in bracket order. */
  labels: string[];
  /** Index of the filled pill. */
  activeIndex: number;
  onSelect: (index: number) => void;
  ariaLabel: string;
}

/**
 * One row of round pills that scrolls sideways inside itself when the rounds
 * do not fit; the filled pill is kept in view.
 */
export const RoundPills: React.FC<RoundPillsProps> = ({ labels, activeIndex, onSelect, ariaLabel }) => {
  const rowRef = useRef<HTMLDivElement>(null);

  // Scrolls the row only, never the page.
  useEffect(() => {
    const row = rowRef.current;
    const pill = row?.children[activeIndex];
    if (!row || !(pill instanceof HTMLElement)) return;
    const left = Math.min(pill.offsetLeft, Math.max(pill.offsetLeft + pill.offsetWidth - row.clientWidth, row.scrollLeft));
    if (left !== row.scrollLeft) row.scrollTo({ left, behavior: 'smooth' });
  }, [activeIndex]);

  return (
    <div ref={rowRef} className={styles.row} role="group" aria-label={ariaLabel} data-testid="bracket-round-pills">
      {labels.map((label, index) => (
        <FilterChip
          key={index}
          label={label}
          pressed={index === activeIndex}
          onClick={() => onSelect(index)}
          className={styles.pill}
        />
      ))}
    </div>
  );
};

export default RoundPills;
